import { useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import Seo from '@/components/Seo';
import { Breadcrumb } from '@/components/layout/Breadcrumb';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { UploadDropzone } from '@/components/tools/UploadDropzone';
import { ToolLongForm } from '@/components/tools/ToolLongForm';
import { ToolFAQ } from '@/components/tools/ToolFAQ';
import { AdSlot } from '@/components/ads/AdSlot';
import { convertWordToPdf, absoluteApiUrl } from '@/lib/api';
import { downloadFromUrl, humanBytes } from '@/lib/download';
import { AlertCircle, CheckCircle2, Download, FileText, Loader2, RotateCcw, ShieldCheck } from 'lucide-react';
import { TOOL_PAGE } from '@/constants/testIds';
import { toast } from 'sonner';

const FAQS = [
    { q: 'Which file formats can I upload?', a: 'Modern .docx files and older .doc files are both supported. Encrypted or password-protected files can’t be processed — remove the password in Word first.' },
    { q: 'How large can my file be?', a: 'Up to 25 MB per upload. That covers most long, image-heavy documents. Need bigger? Split the document into two.' },
    { q: 'Are my documents kept on your servers?', a: 'No. Files are processed in an isolated worker and deleted from disk right after conversion. We never store or reuse your document.' },
    { q: 'Will my formatting stay intact?', a: 'Yes for standard formatting: headings, paragraphs, tables, images, headers, footers, and page numbering. Very complex macros or ActiveX embeds may not render.' },
    { q: 'Can I convert Google Docs?', a: 'Export the Google Doc as a .docx first (File → Download → Microsoft Word), then upload the .docx here.' },
];

const HOW = [
    'Upload your .doc or .docx file using the box on the left. You can drag it in or click to open your file picker. We accept files up to 25 MB and process them on our own servers, so your phone or laptop doesn’t have to do the heavy lifting.',
    'Behind the scenes, UtilityTool opens your document with a headless LibreOffice engine and uses its native PDF exporter to generate a real, standards-compliant PDF. Fonts you’ve embedded are preserved, page sizes and margins stay the same, and page breaks land exactly where Word placed them.',
    'When conversion finishes, you get a Download button. Files are streamed straight to your browser — we never hand out a link that someone else could stumble across. The moment the download starts, our copy of your file is removed from disk.',
    'If you need to convert several documents, do them one at a time — there’s no queue, no captcha, and no waiting on a batch. And because everything runs on our infrastructure, you can convert on a slow phone connection just as fast as on a fibre laptop.',
];
const BENEFITS = [
    'PDFs are the safest way to share formatted documents. They render identically on every device, print exactly as they look on screen, and can’t be accidentally re-edited by a well-meaning reviewer. Converting your Word file to PDF before sending is a small step that saves large amounts of confusion later.',
    'Search engines, ATS systems, and legal archives all prefer PDF for one reason: predictability. A .docx opened on a Mac in 2015 may render slightly differently from a .docx opened on Windows in 2025. A PDF, on the other hand, looks the same in a decade as it does today. That stability is what makes it the backbone of digital paperwork.',
    'UtilityTool keeps things deliberately simple: there’s no signup wall, no watermark, and no premium tier for hidden features. If we can add a feature that helps most people most of the time, we do — and we keep it free.',
];
const TIPS = [
    'Accept all tracked changes in Word before converting to keep the PDF clean.',
    'Embed fonts (File → Options → Save) if your document uses custom typefaces.',
    'For very long files, check that page numbering and headers land on the pages you expect after conversion.',
];

export default function WordToPdfPage() {
    const [file, setFile] = useState(null);
    const [progress, setProgress] = useState(0);
    const [status, setStatus] = useState('idle'); // idle | uploading | done | error
    const [error, setError] = useState('');
    // Instead of a Blob, we now keep the envelope returned by the API:
    // { download_url, filename, size_bytes, ... }
    const [result, setResult] = useState(null);
    const navigate = useNavigate();

    const reset = () => {
        setFile(null);
        setProgress(0);
        setStatus('idle');
        setError('');
        setResult(null);
    };

    const onFile = (f) => {
        setFile(f);
        setStatus('idle');
        setError('');
        setResult(null);
    };

    const run = async () => {
        if (!file) return;
        setStatus('uploading');
        setError('');
        setProgress(0);
        try {
            const envelope = await convertWordToPdf(file, setProgress);
            setResult(envelope);
            setStatus('done');
            toast.success('Your PDF is ready to download.');
        } catch (err) {
            const msg = err?.response?.data?.detail || err?.message || 'Something went wrong.';
            setError(msg);
            setStatus('error');
            toast.error(msg);
        }
    };

    const structured = {
        '@context': 'https://schema.org',
        '@type': 'SoftwareApplication',
        name: 'UtilityTool — Word to PDF',
        applicationCategory: 'Utility',
        operatingSystem: 'Any',
        offers: { '@type': 'Offer', price: '0', priceCurrency: 'USD' },
    };

    return (
        <>
            <Seo
                title="Word to PDF converter"
                description="Convert .doc and .docx files to clean, portable PDFs with server-side rendering. Free, no signup, files auto-deleted after conversion."
                canonical="/tools/word-to-pdf"
                structuredData={structured}
            />
            <section className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 pt-10 pb-14">
                <Breadcrumb items={[{ label: 'Tools', to: '/tools' }, { label: 'Word to PDF' }]} />
                <div className="mt-4 flex items-start gap-4">
                    <span className="inline-flex items-center justify-center w-12 h-12 rounded-xl bg-[hsl(var(--accent))] text-[hsl(var(--primary))]">
                        <FileText className="w-6 h-6" />
                    </span>
                    <div>
                        <h1 className="text-3xl sm:text-4xl font-semibold tracking-tight text-slate-900">
                            Word to PDF converter
                        </h1>
                        <p className="mt-2 text-slate-600 max-w-2xl">
                            Upload a .doc or .docx file and get back a PDF that keeps your
                            fonts, images, and layout intact.
                        </p>
                        <div className="mt-3 flex flex-wrap gap-2">
                            <Badge variant="secondary" className="bg-slate-100 text-slate-700">Server-side</Badge>
                            <Badge variant="secondary" className="bg-slate-100 text-slate-700">Preserves formatting</Badge>
                            <Badge variant="secondary" className="bg-slate-100 text-slate-700">Up to 25 MB</Badge>
                        </div>
                    </div>
                </div>

                <div className="mt-8 grid grid-cols-1 lg:grid-cols-[1.2fr_0.8fr] gap-6 lg:gap-8">
                    <Card className="p-5 sm:p-6 border-border/80">
                        <UploadDropzone
                            accept=".doc,.docx"
                            file={file}
                            onFile={onFile}
                            onClear={reset}
                            disabled={status === 'uploading'}
                            hint="Accepted formats: .doc, .docx · Max 25 MB"
                        />

                        {status === 'uploading' ? (
                            <div className="mt-6">
                                <div className="flex items-center justify-between text-sm mb-2">
                                    <span className="font-medium text-slate-800">Uploading & converting…</span>
                                    <span className="text-slate-500">{progress}%</span>
                                </div>
                                <Progress value={progress} className="h-2" />
                            </div>
                        ) : null}

                        {status === 'error' && error ? (
                            <Alert variant="destructive" className="mt-6" data-testid={TOOL_PAGE.errorAlert}>
                                <AlertCircle className="w-4 h-4" />
                                <AlertTitle>We couldn’t convert this file</AlertTitle>
                                <AlertDescription>{error}</AlertDescription>
                            </Alert>
                        ) : null}

                        {status === 'done' && result ? (
                            <div className="mt-6 rounded-xl border border-emerald-200 bg-emerald-50 p-4" data-testid={TOOL_PAGE.resultCard}>
                                <div className="flex items-center gap-2 text-emerald-700">
                                    <CheckCircle2 className="w-4 h-4" />
                                    <span className="text-sm font-medium">Conversion complete</span>
                                </div>
                                <div className="mt-3 flex items-center justify-between gap-3">
                                    <div className="min-w-0">
                                        <p className="text-sm font-semibold text-slate-900 truncate">{result.filename}</p>
                                        <p className="text-xs text-slate-500">{humanBytes(result.size_bytes)}</p>
                                    </div>
                                    <Button
                                        data-testid={TOOL_PAGE.downloadButton}
                                        onClick={() => downloadFromUrl(absoluteApiUrl(result.download_url), result.filename)}
                                        className="bg-[hsl(var(--primary))] hover:bg-[hsl(var(--link-hover))] text-white"
                                    >
                                        <Download className="w-4 h-4 mr-2" /> Download PDF
                                    </Button>
                                </div>
                            </div>
                        ) : null}

                        <div className="mt-6 flex flex-col sm:flex-row gap-3">
                            <Button
                                onClick={run}
                                disabled={!file || status === 'uploading'}
                                data-testid={TOOL_PAGE.runButton}
                                className="h-11 bg-slate-900 hover:bg-slate-800 text-white"
                            >
                                {status === 'uploading' ? (
                                    <>
                                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                                        Converting…
                                    </>
                                ) : (
                                    <>Convert to PDF</>
                                )}
                            </Button>
                            <Button variant="secondary" onClick={reset} className="h-11">
                                <RotateCcw className="w-4 h-4 mr-2" /> Reset
                            </Button>
                        </div>
                    </Card>

                    <aside className="space-y-4">
                        <Card className="p-5 border-border/80 bg-[hsl(var(--surface-2))]">
                            <div className="flex items-center gap-2 text-slate-800">
                                <ShieldCheck className="w-4 h-4 text-[hsl(var(--primary))]" />
                                <span className="text-sm font-semibold">Your file is safe</span>
                            </div>
                            <p className="mt-2 text-sm text-slate-600 leading-relaxed">
                                Files are processed in an isolated worker and deleted from disk
                                immediately after conversion. We never store, log, or reuse
                                your document.
                            </p>
                        </Card>
                        <Card className="p-5 border-border/80">
                            <p className="text-sm font-semibold text-slate-900">Also useful</p>
                            <ul className="mt-2 space-y-1.5 text-sm">
                                <li>
                                    <a href="/tools/image-compressor" className="text-[hsl(var(--primary))] hover:underline">Image Compressor</a>
                                </li>
                                <li>
                                    <a href="/tools/jpg-to-png" className="text-[hsl(var(--primary))] hover:underline">JPG to PNG</a>
                                </li>
                                <li>
                                    <a href="/blog/convert-word-to-pdf-online-safely" className="text-[hsl(var(--primary))] hover:underline">Guide: convert Word to PDF without losing formatting</a>
                                </li>
                            </ul>
                        </Card>
                    </aside>
                </div>

                <ToolLongForm howItWorks={HOW} benefits={BENEFITS} tips={TIPS} />
                <AdSlot placement="tool" />
                <ToolFAQ items={FAQS} />
            </section>
        </>
    );
}
